import React, { useEffect, useState } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  GiftOutlined,
  MailOutlined,
  RobotOutlined,
  ShopOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import { useTranslation } from '../../../i18n';
import { getCatalogEntitlementPackages } from '../../../store/request';
import type {
  EntitlementOfferPrice,
  EntitlementOfferSpecification,
  EntitlementPackageOffer
} from '../../../types/catalog';
import { EntitlementPurchaseModal } from './EntitlementPurchaseModal';
import { currencySymbol, featureValue, formatMinor } from './pricingUtils';

const ENTITLEMENT_ICONS = [
  RobotOutlined,
  VideoCameraOutlined,
  ShopOutlined,
  MailOutlined,
  GiftOutlined
] as const;

function moneyText(price?: EntitlementOfferPrice) {
  const amount = formatMinor(price?.amountMinor, price?.currencyFractionDigits);
  if (!amount) return '';
  const symbol = currencySymbol(price?.currency);
  return symbol ? `${symbol}${amount}` : amount;
}

function validityText(
  days: number | null | undefined,
  t: (key: string, options?: { count: number }) => string
) {
  if (typeof days === 'number' && Number.isFinite(days)) return t('pricing.days', { count: days });
  return t('pricing.permanent');
}

/** 规格切换文案：quotaLimit + quotaUnit，如 1个 / 2个。 */
function specLabel(spec: EntitlementOfferSpecification, quotaUnit?: string) {
  const limit =
    typeof spec.quotaLimit === 'number' && Number.isFinite(spec.quotaLimit)
      ? String(spec.quotaLimit)
      : '';
  const unit = quotaUnit?.trim() ?? '';
  if (limit && unit) return `${limit}${unit}`;
  return limit || '—';
}

function FeatureMark({ value }: { value: string }) {
  if (value === 'check') return <CheckOutlined className='pricing-feature-check' />;
  if (value === 'cross') return <CloseOutlined className='pricing-feature-cross' />;
  return <span className='pricing-feature-quota'>{value}</span>;
}

export function EntitlementPricing() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<EntitlementPackageOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpec, setSelectedSpec] = useState<Record<string, number>>({});
  const [selectedSku, setSelectedSku] = useState<Record<string, string>>({});
  const [activeKey, setActiveKey] = useState<string>();
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchasePackageCode, setPurchasePackageCode] = useState<string>();
  const [purchaseSkuCode, setPurchaseSkuCode] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCatalogEntitlementPackages()
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !Array.isArray(res.content)) {
          if (res.code !== -1) message.error(res.message || t('pricing.packages_load_failed'));
          setPackages([]);
          return;
        }
        setPackages(res.content);
        const firstKey = res.content[0]?.packageCode?.trim() || 'package-0';
        setActiveKey(firstKey);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('pricing.packages_load_failed'));
          setPackages([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const chosenPrice = (spec: EntitlementOfferSpecification | undefined, priceKey: string) => {
    const prices = spec?.prices ?? [];
    const selected = selectedSku[priceKey];
    if (!selected) return prices[0];
    return prices.find((item, index) => (item.skuCode?.trim() || `price-${index}`) === selected) ?? prices[0];
  };

  const openPurchase = (packageCode?: string, skuCode?: string) => {
    setPurchasePackageCode(packageCode);
    setPurchaseSkuCode(skuCode);
    setPurchaseOpen(true);
  };

  return (
    <div className='ml-6'>
      <div className='container pricing-page entitlement-page'>
        <header className='pricing-header'>
          <h1 className='pricing-title'>{t('layout.buy_entitlement')}</h1>
        </header>

        {loading ? null : packages.length === 0 ? (
          <p className='m-0 text-sm text-slate-400'>{t('pricing.no_packages')}</p>
        ) : (
          <div className='entitlement-grid'>
            {packages.map((offer, index) => {
              const packageKey = offer.packageCode?.trim() || `package-${index}`;
              const specs = offer.specifications ?? [];
              const specIndex =
                specs.length === 0 ? 0 : Math.min(selectedSpec[packageKey] ?? 0, specs.length - 1);
              const spec = specs[specIndex];
              const priceKey = `${packageKey}:${specIndex}`;
              const prices = spec?.prices ?? [];
              const price = chosenPrice(spec, priceKey);
              const amount = moneyText(price);
              const canBuy =
                Boolean(price?.skuCode?.trim()) &&
                typeof price?.amountMinor === 'number' &&
                Number.isFinite(price.amountMinor);
              const packageName = offer.displayName?.trim() || '—';
              const features = offer.featureDescriptions ?? [];
              const quotaUnit = offer.quotaUnit?.trim() ?? '';
              const Icon = ENTITLEMENT_ICONS[index % ENTITLEMENT_ICONS.length];
              const isActive = activeKey === packageKey;
              return (
                <article
                  key={packageKey}
                  className={`entitlement-card${isActive ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveKey(packageKey)}
                  onFocus={() => setActiveKey(packageKey)}
                >
                  <div className='entitlement-card-top'>
                    <div className='entitlement-card-head'>
                      <span className={`entitlement-card-icon entitlement-card-icon--${index % 5}`} aria-hidden>
                        <Icon />
                      </span>
                      <h2 className='entitlement-card-name'>{packageName}</h2>
                    </div>

                    {features.length > 0 ? (
                      <ul className='entitlement-card-features'>
                        {features.map((feature, featureIndex) => {
                          const title = feature.title?.trim() || '—';
                          const key = `${feature.title ?? 'feature'}-${featureIndex}`;
                          if (feature.displayType === 'TEXT') {
                            return (
                              <li key={key} className='entitlement-card-feature is-text'>
                                <span className='entitlement-card-feature-label'>{title}</span>
                                <p className='entitlement-card-feature-text'>{feature.text?.trim() || '—'}</p>
                              </li>
                            );
                          }
                          const value = featureValue(feature, t);
                          return (
                            <li key={key} className='entitlement-card-feature'>
                              <span className='entitlement-card-feature-label'>{title}</span>
                              {value ? <FeatureMark value={value} /> : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}

                  </div>

                  <div className='entitlement-card-purchase'>
                    {specs.length > 1 ? (
                      <div className='entitlement-card-options'>
                        <div className='entitlement-option-label'>{t('pricing.select_spec')}</div>
                        <div
                          className={`entitlement-spec-grid${specs.length <= 2 ? ' is-two' : specs.length === 3 ? ' is-three' : ' is-four'
                            }`}
                          role='tablist'
                          aria-label={t('pricing.select_spec')}
                        >
                          {specs.map((item, itemIndex) => {
                            const itemAmount = moneyText(item.prices?.[0]);
                            const active = itemIndex === specIndex;
                            return (
                              <button
                                key={`spec-${item.quotaLimit ?? itemIndex}-${itemIndex}`}
                                type='button'
                                role='tab'
                                aria-selected={active}
                                className={`entitlement-spec-option${active ? ' is-active' : ''}`}
                                onClick={() =>
                                  setSelectedSpec((current) => ({
                                    ...current,
                                    [packageKey]: itemIndex
                                  }))
                                }
                              >
                                <span className='entitlement-spec-option-quota'>
                                  {specLabel(item, quotaUnit)}
                                </span>
                                {itemAmount ? (
                                  <span className='entitlement-spec-option-price'>{itemAmount}</span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {prices.length > 1 ? (
                      <div className='entitlement-switch' role='tablist'>
                        {prices.map((item, priceIndex) => {
                          const sku = item.skuCode?.trim() || `price-${priceIndex}`;
                          const selected = item === price;
                          return (
                            <button
                              key={sku}
                              type='button'
                              className={`entitlement-switch-btn${selected ? ' is-active' : ''}`}
                              onClick={() =>
                                setSelectedSku((current) => ({
                                  ...current,
                                  [priceKey]: sku
                                }))
                              }
                            >
                              {moneyText(item) || '—'}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className='entitlement-card-footer'>
                      <div className='entitlement-card-price'>
                        <span className='entitlement-card-amount'>{amount || t('pricing.no_price')}</span>
                        {spec ? (
                          <span className='entitlement-card-validity'>/ {validityText(spec.validityDays, t)}</span>
                        ) : null}
                      </div>
                      <button
                        type='button'
                        className={`entitlement-buy${canBuy ? '' : ' is-disabled'}`}
                        disabled={!canBuy}
                        onClick={() => openPurchase(offer.packageCode?.trim(), price?.skuCode?.trim())}
                      >
                        {canBuy ? t('pricing.buy_now') : t('pricing.unavailable')}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <EntitlementPurchaseModal
        open={purchaseOpen}
        packages={packages}
        initialPackageCode={purchasePackageCode}
        initialSkuCode={purchaseSkuCode}
        onClose={() => setPurchaseOpen(false)}
        onPaid={() => setPurchaseOpen(false)}
      />
    </div>
  );
}
